import { useState } from 'react';
import * as XLSX from 'xlsx';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@material-ui/core';
import FileInput from 'components/FileInput';
import { sendMassiveProductsLoad } from 'services/productServices';
import { excelValidation } from 'utils/yup';
import { normalizeKey } from 'utils/strings';
import Product from 'types/models/Product';

function MassiveLoad({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const { t, i18n } = useTranslation();
  const [successRows, setSuccessRows] = useState<{ line: number; product: Product } | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [error, setError] = useState<{
    errorKey: string | null;
    errorValue: string | null;
    errorValues?: Record<string, any>;
    line?: number;
    product?: Product;
  } | null>(null);

  const parseExcelToNormalizedJson = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          const rawData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          const normalizedData = rawData.map((row: any) => {
            const newRow: any = {};
            Object.keys(row).forEach((key) => {
              const newKey = normalizeKey(key);
              newRow[newKey] = row[key];
            });
            return newRow;
          });

          resolve(normalizedData);
        } catch (err) {
          reject(err);
        }
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const onSubmit = async (values: any) => {
    const file = values.file;
    const updatedProducts = await parseExcelToNormalizedJson(file);

    if (updatedProducts.length === 0) {
      setError({
        errorKey: 'no-products-in-file',
        errorValue: '',
      });
      return;
    }

    try {
      const { error, lastSuccessRow } = await sendMassiveProductsLoad({
        products: updatedProducts,
        lang: i18n.language,
      });

      setSuccessRows(lastSuccessRow);
      formik.setFieldValue('file', null);
      setFileKey((prev) => prev + 1);
      if (lastSuccessRow) onSuccess();
      if (error) {
        setError(error);
        return;
      }

      setError(null);
    } catch (err) {
      setSuccessRows(null);
      setError({
        errorKey: 'default-error',
        errorValue: '',
      });
    } finally {
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      file: null,
    },
    validationSchema: yup.object().shape({
      file: excelValidation(true, t),
    }),
    onSubmit,
  });

  const ErrorMessage = () => {
    if (!error?.errorKey) return null;

    if (error.errorKey === 'default-error' || error.errorKey === 'no-products-in-file') {
      return (
        <Typography color="error">{t(`massive-load.error.${error.errorKey}`, { value: error.errorValue })}.</Typography>
      );
    }

    const errors = { value: error.errorValue, ...error.errorValues };

    return (
      <>
        <Typography color="error">
          {t(`massive-load.error.for-line`)} {error.line}
          {error.product?.name ? ` (${t('massive-load.product')}: ${error.product?.name})` : ''}:{' '}
          {t(`massive-load.error.${error.errorKey}`, errors)}.
        </Typography>
        {i18n.exists(`massive-load.error.${error.errorKey}-2`) && (
          <Typography color="error">{t(`massive-load.error.${error.errorKey}-2`, errors)}.</Typography>
        )}
      </>
    );
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit} className="massive-load-form-container">
        <FileInput
          key={fileKey}
          label={t('massive-load.select-excel')}
          name="file"
          isSubmitting={formik.isSubmitting}
          value={formik.values.file}
          onChange={() => {
            setError(null);
            setSuccessRows(null);
          }}
          setFieldValue={formik.setFieldValue}
          touched={formik.touched.file}
          errors={formik.errors.file}
          color="inherit"
          variant="contained"
          quitFileColor="primary"
        />
        <Button
          fullWidth
          type="submit"
          variant="contained"
          color="primary"
          disabled={formik.isSubmitting || !formik.values.file}
        >
          {t('massive-load.upload-excel')}
        </Button>
      </form>

      {formik.isSubmitting && <Typography color="primary">{t('loading')}</Typography>}

      {successRows?.line && (
        <Typography color="primary">
          {t('massive-load.success-until')} {successRows.line}.
          {successRows.product?.name ? ` (${successRows.product?.name})` : ''}
        </Typography>
      )}

      <ErrorMessage />
    </>
  );
}

export default MassiveLoad;
